'use client';

import { useMemo, useState } from 'react';

import { useWindowSize } from 'codekit';
import { motion } from 'framer-motion';

import { Icon } from 'components/Icon';
import Switcher from 'components/Switcher';
import { social } from 'constants/institutional';
import { commitsStats, freePlan } from 'constants/pricing';
import Button from 'layouts/institucional/Button';
import CommitsStatsCurvedArrow from 'layouts/institucional/Elements/CommitsStatsCurvedArrow';
import { LuminousAnimationHandler } from 'layouts/institucional/Elements/LuminousAnimation';
import PlanElement from 'layouts/institucional/Elements/Plan';
import Section from 'layouts/institucional/Section';
import Text from 'layouts/institucional/Section/Elements/Text';
import { periodsMap, Plan, PlanPeriod } from 'models/Plan';
import { getAnimationProps } from 'utils/motion/getAnimationProps';
import { toOptions } from 'utils/toOptions';

import styles from './styles.module.css';

interface PricingProps {
	plans: Plan[];
}

function Pricing(props: PricingProps) {
	// Hooks
	const size = useWindowSize();

	// States
	const [period, setPeriod] = useState<PlanPeriod>('monthly');

	// Memo vars
	const title = useMemo(() => {
		return size.isMobile
			? 'Planos alinhados com as suas necessidades'
			: 'Escolha o plano que <br />é alinhado com suas necessidades';
	}, [size.isMobile]);

	return (
		<>
			<Section className={styles.container}>
				<Text align="center" heading>
					<motion.div {...getAnimationProps('blur_in', 1)}>
						<Switcher
							optionByValue={period}
							options={toOptions(periodsMap)}
							onChange={(period) => setPeriod(period)}
						/>
					</motion.div>

					<motion.h2
						style={{ fontSize: '44px', marginTop: '8px' }}
						{...getAnimationProps('blur_in', 2)}
					>
						<span dangerouslySetInnerHTML={{ __html: title }} />
					</motion.h2>
					<motion.p {...getAnimationProps('blur_in', 3)}>
						Encontre o plano que mais dá match com suas necessidades
						e dê <br />
						superpoderes para seus commits.
					</motion.p>
				</Text>

				<div className={styles.plans}>
					{[freePlan, props.plans].flat().map((plan, index) => (
						<PlanElement
							key={plan.id}
							plan={plan}
							period={period}
							delayIndex={index + 1 + 2 + 1}
							highligh={index === 1}
						/>
					))}
				</div>

				<div className={styles.commit_stats}>
					<CommitsStatsCurvedArrow
						className={styles.stat_arrow_left}
					/>
					<CommitsStatsCurvedArrow
						className={styles.stat_arrow_right}
					/>

					<div className={styles.commit_stats__row}>
						<div
							className={styles.commit_stats__row__cell}
							data-highlight="true"
						>
							Senioridade
						</div>

						{commitsStats.map((stat) => (
							<div
								key={stat.seniority}
								className={styles.commit_stats__row__cell}
								data-highlight="true"
							>
								{stat.seniority}
							</div>
						))}
					</div>

					<div className={styles.commit_stats__row}>
						<div
							className={styles.commit_stats__row__cell}
							data-highlight="true"
						>
							Média (mês)
						</div>

						{commitsStats.map((stat) => (
							<div
								key={stat.seniority}
								className={styles.commit_stats__row__cell}
							>
								{stat.minCommits} - {stat.maxCommits} commits
							</div>
						))}
					</div>
				</div>

				<LuminousAnimationHandler />
			</Section>

			<Section>
				<Text align="center">
					<h2 style={{ fontSize: '44px', marginTop: '8px' }}>
						Não encontrou o <br />
						plano ideal para você?
					</h2>
					<p>
						Entre em contato com a gente que vamos criar um plano
						customizado <br />
						para você, adaptado as suas necessidades
					</p>

					<a
						href={social.whatsapp}
						target="_blank"
						rel="noreferrer noopener"
						aria-label="Fale conosco pelo WhatsApp"
					>
						<Button variant="default">
							Entrar em contato <Icon name="Whatsapp" />
						</Button>
					</a>
				</Text>
			</Section>
		</>
	);
}

export default Pricing;
